import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { MessageSquare, Trash2, Send, LogIn, ThumbsUp, ThumbsDown, Reply, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
};

const SingleComment = ({ comment, depth, onDelete, onReply, replyingTo, onCancelReply, onSubmitReply, replyText, onReplyTextChange, submittingReply, user }) => {
  const [userVote, setUserVote] = useState(null);
  const [likeCount, setLikeCount] = useState(comment.like_count || 0);
  const [dislikeCount, setDislikeCount] = useState(comment.dislike_count || 0);

  useEffect(() => {
    const votes = JSON.parse(localStorage.getItem('comment_votes') || '{}');
    if (votes[comment.id]) setUserVote(votes[comment.id]);
  }, [comment.id]);

  const handleVote = async (voteType) => {
    try {
      const votes = JSON.parse(localStorage.getItem('comment_votes') || '{}');
      if (userVote === voteType) {
        const endpoint = voteType === 'like' ? 'unlike' : 'undislike';
        const { data } = await axios.post(`${BACKEND_URL}/api/comments/${comment.id}/${endpoint}`);
        setLikeCount(data.like_count);
        setDislikeCount(data.dislike_count);
        delete votes[comment.id];
        setUserVote(null);
      } else {
        if (userVote) {
          const undoEndpoint = userVote === 'like' ? 'unlike' : 'undislike';
          await axios.post(`${BACKEND_URL}/api/comments/${comment.id}/${undoEndpoint}`);
        }
        const { data } = await axios.post(`${BACKEND_URL}/api/comments/${comment.id}/${voteType}`);
        setLikeCount(data.like_count);
        setDislikeCount(data.dislike_count);
        votes[comment.id] = voteType;
        setUserVote(voteType);
      }
      localStorage.setItem('comment_votes', JSON.stringify(votes));
    } catch (error) {
      toast.error("Failed to register vote");
    }
  };

  const isReplying = replyingTo === comment.id;
  const indent = Math.min(depth, 3);

  return (
    <div className="mt-3" style={{ marginLeft: `${indent * 24}px` }} data-testid={`comment-${comment.id}`}>
      {depth > 0 && (
        <div className="flex items-center gap-1 mb-1 text-xs" style={{ color: "var(--color-text-dim)" }}>
          <Reply className="w-3 h-3 rotate-180" />
          <span>replying to {comment.parent_user_name || "a comment"}</span>
        </div>
      )}
      <div
        className="rounded-lg p-4"
        style={{
          backgroundColor: depth > 0 ? "var(--color-card-hover)" : "var(--color-card)",
          border: "1px solid var(--color-border)",
          opacity: depth > 0 ? 0.95 : 1,
        }}
      >
        <div className="flex items-start justify-between mb-2">
          <div>
            <span className="font-semibold text-sm" style={{ color: "var(--color-text)" }}>@{comment.user_name}</span>
            <span className="text-xs ml-2" style={{ color: "var(--color-text-dim)" }}>{formatTime(comment.created_at)}</span>
          </div>
          {user && user.id === comment.user_id && (
            <button onClick={() => onDelete(comment.id)} className="hover:text-red-400 transition-colors" style={{ color: "var(--color-text-dim)" }} data-testid={`delete-comment-${comment.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
        <p className="text-sm leading-relaxed whitespace-pre-wrap mb-3" style={{ color: "var(--color-text-muted)" }}>{comment.text}</p>
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleVote('like')}
            className="flex items-center gap-1 px-2 py-1 rounded transition-colors text-xs"
            style={{
              backgroundColor: userVote === 'like' ? "var(--color-accent)" : "var(--color-card-hover)",
              color: userVote === 'like' ? "#fff" : "var(--color-text-muted)",
            }}
            data-testid={`like-button-${comment.id}`}
          >
            <ThumbsUp className="w-3 h-3" />
            <span className="font-semibold">{likeCount}</span>
          </button>
          <button
            onClick={() => handleVote('dislike')}
            className="flex items-center gap-1 px-2 py-1 rounded transition-colors text-xs"
            style={{
              backgroundColor: userVote === 'dislike' ? "#ef4444" : "var(--color-card-hover)",
              color: userVote === 'dislike' ? "#fff" : "var(--color-text-muted)",
            }}
            data-testid={`dislike-button-${comment.id}`}
          >
            <ThumbsDown className="w-3 h-3" />
            <span className="font-semibold">{dislikeCount}</span>
          </button>
          {user && user !== false && (
            <button
              onClick={() => onReply(comment.id)}
              className="flex items-center gap-1 px-2 py-1 rounded transition-colors text-xs"
              style={{ backgroundColor: "var(--color-card-hover)", color: "var(--color-text-muted)" }}
              data-testid={`reply-button-${comment.id}`}
            >
              <Reply className="w-3 h-3" />
              <span className="font-semibold">Reply</span>
            </button>
          )}
        </div>
      </div>

      {isReplying && (
        <div className="mt-2 ml-6" data-testid={`reply-form-${comment.id}`}>
          <div className="rounded-lg p-3" style={{ backgroundColor: "var(--color-card-hover)", border: "1px solid var(--color-border)" }}>
            <div className="text-xs mb-2" style={{ color: "var(--color-text-dim)" }}>Replying to {comment.user_name}</div>
            <Textarea
              value={replyText}
              onChange={(e) => onReplyTextChange(e.target.value)}
              placeholder="Write your reply..."
              className="w-full mb-2 min-h-[70px] text-sm"
              style={{ backgroundColor: "var(--color-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
              data-testid={`reply-input-${comment.id}`}
            />
            <div className="flex justify-end gap-2">
              <Button onClick={onCancelReply} variant="ghost" size="sm" style={{ color: "var(--color-text-dim)" }} data-testid={`cancel-reply-${comment.id}`}>
                Cancel
              </Button>
              <Button
                onClick={() => onSubmitReply(comment.id)}
                disabled={submittingReply || !replyText.trim()}
                size="sm"
                className="text-white"
                style={{ backgroundColor: "var(--color-accent)" }}
                data-testid={`submit-reply-${comment.id}`}
              >
                <Send className="w-3 h-3 mr-1" />
                {submittingReply ? "Posting..." : "Reply"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Comments = ({ articleUrl, onAuthClick }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [submittingReply, setSubmittingReply] = useState(false);

  const fetchCommentCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/comments/count?article_url=${encodeURIComponent(articleUrl)}`);
      setCommentCount(data.count);
    } catch (error) { console.error("Error fetching comment count:", error); }
  }, [articleUrl]);

  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/comments?article_url=${encodeURIComponent(articleUrl)}`);
      const flatList = [];
      const flatten = (items, depth) => {
        for (const item of items) {
          flatList.push({ ...item, _depth: depth });
          if (item.replies && item.replies.length > 0) flatten(item.replies, depth + 1);
        }
      };
      flatten(data.comments, 0);
      setComments(flatList);
    } catch (error) { console.error("Error fetching comments:", error); }
    finally { setLoading(false); }
  }, [articleUrl]);

  useEffect(() => { fetchCommentCount(); }, [fetchCommentCount]);
  useEffect(() => { if (showComments) fetchComments(); }, [showComments, fetchComments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/comments`, { article_url: articleUrl, text: commentText, parent_comment_id: null }, { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
      setCommentText("");
      fetchComments();
      fetchCommentCount();
      toast.success("Comment posted!");
    } catch (error) { toast.error("Failed to post comment"); }
    finally { setSubmitting(false); }
  };

  const handleReply = (commentId) => { setReplyingTo(commentId); setReplyText(""); };
  const handleCancelReply = () => { setReplyingTo(null); setReplyText(""); };

  const handleSubmitReply = async (parentId) => {
    if (!replyText.trim()) return;
    setSubmittingReply(true);
    try {
      await axios.post(`${BACKEND_URL}/api/comments`, { article_url: articleUrl, text: replyText, parent_comment_id: parentId }, { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
      setReplyingTo(null);
      setReplyText("");
      fetchComments();
      fetchCommentCount();
      toast.success("Reply posted!");
    } catch (error) { toast.error("Failed to post reply"); }
    finally { setSubmittingReply(false); }
  };

  const handleDelete = async (commentId) => {
    if (!confirm("Delete this comment?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/comments/${commentId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
      fetchComments();
      fetchCommentCount();
      toast.success("Comment deleted");
    } catch (error) { toast.error("Failed to delete comment"); }
  };

  return (
    <div className="mt-6 pt-6" style={{ borderTop: "1px solid var(--color-border)" }}>
      <button
        onClick={() => setShowComments(!showComments)}
        className="flex items-center gap-2 font-semibold mb-4 transition-colors"
        style={{ color: "var(--color-text)" }}
        data-testid="toggle-comments"
      >
        <MessageSquare className="w-5 h-5" />
        <span>{commentCount} {commentCount === 1 ? 'Comment' : 'Comments'}</span>
        {showComments ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {showComments && (
        <div>
          {user && user !== false ? (
            <form onSubmit={handleSubmit} className="mb-6" data-testid="comment-form">
              <Textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Share your thoughts..."
                className="w-full mb-2 min-h-[100px]"
                style={{ backgroundColor: "var(--color-input-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
                data-testid="comment-input"
              />
              <div className="flex justify-end">
                <Button type="submit" disabled={submitting || !commentText.trim()} className="text-white" style={{ backgroundColor: "var(--color-accent)" }} data-testid="submit-comment">
                  <Send className="w-4 h-4 mr-2" />
                  {submitting ? "Posting..." : "Post Comment"}
                </Button>
              </div>
            </form>
          ) : (
            <div className="mb-6 p-4 rounded-lg text-center" style={{ backgroundColor: "var(--color-card-hover)", border: "1px solid var(--color-border)" }}>
              <p className="mb-3" style={{ color: "var(--color-text-muted)" }}>Sign in to join the discussion</p>
              <Button onClick={onAuthClick} className="text-white" style={{ backgroundColor: "var(--color-accent)" }} data-testid="comment-signin-button">
                <LogIn className="w-4 h-4 mr-2" />
                Sign In to Comment
              </Button>
            </div>
          )}

          {loading ? (
            <div className="text-center py-8" style={{ color: "var(--color-text-dim)" }}>Loading comments...</div>
          ) : comments.length === 0 ? (
            <div className="text-center py-8" style={{ color: "var(--color-text-dim)" }}>
              <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No comments yet. Be the first to comment!</p>
            </div>
          ) : (
            <div data-testid="comments-list">
              {comments.map((comment) => (
                <SingleComment
                  key={comment.id}
                  comment={comment}
                  depth={comment._depth || 0}
                  onDelete={handleDelete}
                  onReply={handleReply}
                  replyingTo={replyingTo}
                  onCancelReply={handleCancelReply}
                  onSubmitReply={handleSubmitReply}
                  replyText={replyingTo === comment.id ? replyText : ""}
                  onReplyTextChange={setReplyText}
                  submittingReply={submittingReply}
                  user={user}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Comments;
